<template>
  <q-page>
    <!-- Header -->
    <div
      class="row items-center q-pa-sm shadow-1"
      :class="$q.dark.isActive ? 'bg-grey-9' : 'bg-grey-3'"
    >
      <div class="text-subtitle1 text-weight-medium">
        {{ adminMode ? 'Admin Processes' : 'Bulletin Board' }}
      </div>
      <q-space />
      <q-btn
        v-if="$store.getters.isSuperAdmin"
        unelevated
        dense
        icon="list"
        color="secondary"
        padding="xs md"
        @click="showCategoryDialog = true"
      />
      <q-btn
        unelevated
        dense
        icon="filter_list"
        padding="xs md"
        color="secondary"
        class="q-ml-sm"
      >
        <q-menu :offset="[0, 8]">
          <q-list dense style="min-width: 160px">
            <q-item tag="label">
              <q-item-section side>
                <q-checkbox
                  :value="allSelected"
                  @input="toggleAll"
                  color="secondary"
                  dense
                />
              </q-item-section>
              <q-item-section>All</q-item-section>
            </q-item>
            <q-separator />
            <q-item
              v-for="cat in accessibleCategories"
              :key="cat.id"
              tag="label"
            >
              <q-item-section side>
                <q-checkbox
                  :value="selectedCategories.includes(cat.id)"
                  @input="toggleCategory(cat.id)"
                  :color="cat.color || 'secondary'"
                  dense
                />
              </q-item-section>
              <q-item-section>
                {{ cat.item }}
              </q-item-section>
            </q-item>
            <q-separator />
            <q-item tag="label">
              <q-item-section side>
                <q-checkbox v-model="showHidden" color="grey" dense />
              </q-item-section>
              <q-item-section>Show Hidden</q-item-section>
            </q-item>
          </q-list>
        </q-menu>
      </q-btn>
      <q-btn
        unelevated
        dense
        icon="add"
        color="primary"
        padding="xs md"
        class="q-ml-sm"
        @click="addBulletin()"
      />
    </div>

    <!-- Category sections -->
    <div
      class="q-pa-md scroll"
      :style="`height: ${$store.state.pageHeight - 98}px`"
    >
      <div
        v-for="cat in visibleCategories"
        :key="cat.id"
        class="q-mb-md bulletin-col"
      >
        <div
          class="row items-center q-px-sm q-py-sm q-mb-xs rounded-borders"
          :class="$q.dark.isActive ? 'bg-grey-9' : 'bg-grey-3'"
          :style="`border-left: 4px solid ${categoryHex(cat)}`"
        >
          <div class="text-subtitle2 text-weight-bold">
            {{ cat.item }}
          </div>
          <q-space />
          <q-btn
            unelevated
            dense
            round
            size="sm"
            :icon="isCategoryCollapsed(cat.id) ? 'unfold_more' : 'unfold_less'"
            :color="$q.dark.isActive ? 'grey-8' : 'grey-4'"
            :text-color="$q.dark.isActive ? 'grey-4' : 'grey-7'"
            @click="toggleCategoryCollapse(cat.id)"
          />
        </div>
        <draggable
          :list="categoryItems(cat.id)"
          group="bulletins"
          handle=".drag-handle"
          @end="onDragEnd"
          :data-category="cat.id"
          class="row q-col-gutter-sm q-mt-xs"
          style="min-height: 4px"
        >
          <div v-for="b in categoryItems(cat.id)" :key="b.id" class="col-12">
            <div class="row no-wrap">
              <div
                :style="`width: 4px; min-width: 4px; background: ${categoryHex(
                  cat
                )}; border-radius: 4px 0 0 4px`"
              />
              <div
                style="
                  border: 1px solid rgba(0, 0, 0, 0.12);
                  border-left: none;
                  border-radius: 0 4px 4px 0;
                  background: white;
                  flex: 1;
                  min-width: 0;
                "
              >
                <!-- Collapsed view -->
                <div
                  v-if="isBulletinCollapsed(cat.id, b.id)"
                  class="q-pa-sm cursor-pointer"
                  @click="toggleBulletinExpand(b.id)"
                >
                  <div class="text-body2 ellipsis">
                    {{ b.title || stripHtml(b.body) }}
                  </div>
                </div>
                <!-- Expanded view -->
                <div v-else class="row no-wrap">
                  <div class="col column justify-between q-pa-sm">
                    <div>
                      <div class="row items-center q-mb-xs">
                        <q-icon
                          name="drag_indicator"
                          class="drag-handle text-grey-5 q-mr-xs"
                        />
                        <div
                          v-if="b.attachments && b.attachments.length > 0"
                          class="q-ml-xs text-primary row inline items-center no-wrap"
                          style="
                            background: #eee;
                            padding: 1.5px 4px;
                            font-size: 11px;
                            line-height: 1.3;
                            border-radius: 3px;
                          "
                        >
                          <q-icon
                            name="photo_library"
                            size="12px"
                            class="q-mr-xs"
                          />
                          {{ b.attachments.length }}
                        </div>
                        <span class="text-caption text-grey q-ml-sm">
                          {{ formatDate(b.date) }}
                        </span>
                      </div>
                      <div v-if="b.title" class="text-body2 text-weight-bold">
                        {{ b.title }}
                      </div>
                      <div
                        v-if="b.body"
                        class="text-body2 bulletin-body"
                        v-html="b.body"
                        v-sortable-list="(h) => onListReorder(b, h)"
                        @click="handleBodyClick($event, b)"
                      />
                    </div>
                    <div class="row items-center q-mt-sm">
                      <q-btn
                        v-if="farmName(b.farm_id)"
                        unelevated
                        dense
                        no-caps
                        color="grey-2"
                        text-color="accent"
                        :label="farmName(b.farm_id)"
                        class="q-mr-xs"
                        padding="1px 4px"
                        style="min-height: 0; font-size: 11px; line-height: 1.3"
                        @click="
                          $router.push('/farm/' + b.farm_id + '/bulletins')
                        "
                      />
                      <q-btn
                        unelevated
                        dense
                        no-caps
                        color="grey-2"
                        text-color="primary"
                        padding="1px 4px"
                        style="min-height: 0; font-size: 11px; line-height: 1.3"
                      >
                        {{ bulletinAccessLabel(b) }}
                      </q-btn>
                    </div>
                  </div>
                  <div class="column justify-end q-pa-sm" style="gap: 4px">
                    <q-btn
                      unelevated
                      icon="photo_library"
                      size="sm"
                      color="grey-2"
                      :text-color="
                        b.attachments && b.attachments.length
                          ? 'primary'
                          : 'grey-6'
                      "
                      @click="openAttachments(b)"
                    />
                    <q-btn
                      unelevated
                      icon="edit"
                      size="sm"
                      color="grey-2"
                      text-color="primary"
                      @click="editBulletin(b)"
                    />
                    <q-btn
                      unelevated
                      icon="delete"
                      size="sm"
                      color="grey-2"
                      text-color="negative"
                      @click="confirmDelete(b)"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </draggable>
      </div>
    </div>

    <!-- Create / Edit dialog -->
    <q-dialog persistent v-model="showDialog" full-height position="top">
      <q-card style="width: 500px; max-width: 95vw">
        <div class="text-h6 text-center q-pa-md">
          {{ editItem.id === 'new' ? 'New' : 'Edit' }}
          Bulletin
        </div>
        <q-separator />
        <div
          class="scroll overflow-auto"
          style="max-height: calc(100vh - 150px)"
        >
          <q-form @submit="onSubmit" class="q-gutter-sm q-pa-md">
            <div>
              <q-btn
                unelevated
                dense
                no-caps
                size="sm"
                :color="categoryColor(editItem.category_id)"
                :label="categoryName(editItem.category_id)"
                padding="xs sm"
              >
                <q-icon name="arrow_drop_down" size="16px" right />
                <q-menu :offset="[0, 4]">
                  <q-list dense separator style="min-width: 160px">
                    <q-item
                      v-for="cat in accessibleCategories"
                      :key="cat.id"
                      clickable
                      v-close-popup
                      :active="editItem.category_id === cat.id"
                      active-class="text-teal"
                      @click="editItem.category_id = cat.id"
                    >
                      <q-item-section>
                        {{ cat.item }}
                      </q-item-section>
                      <q-item-section
                        side
                        v-if="editItem.category_id === cat.id"
                      >
                        <q-icon name="check" color="teal" size="xs" />
                      </q-item-section>
                    </q-item>
                  </q-list>
                </q-menu>
              </q-btn>
            </div>
            <q-input
              v-model="editItem.title"
              label="Title (optional)"
              outlined
            />
            <q-editor
              v-model="editItem.body"
              min-height="5rem"
              :toolbar="[
                ['bold', 'italic', 'underline'],
                ['unordered', 'ordered'],
                [
                  {
                    icon: 'format_align_left',
                    fixedLabel: true,
                    options: ['left', 'center', 'right', 'justify']
                  }
                ],
                ['undo', 'redo']
              ]"
            />
            <q-select
              v-model="editItem.farm_id"
              :options="FarmOptions"
              label="Farm (optional)"
              outlined
              clearable
              emit-value
              map-options
            />
            <div
              class="q-pa-sm rounded-borders"
              style="border: 1px solid #e0e0e0"
            >
              <q-checkbox
                v-model="editItem.display_default"
                label="Display on Main Board"
                :true-value="1"
                :false-value="0"
                dense
              />
            </div>
            <div
              class="q-mt-sm q-pa-sm rounded-borders"
              style="border: 1px solid #e0e0e0"
            >
              <q-checkbox
                v-model="editItem.all_users"
                label="All Users"
                :true-value="1"
                :false-value="0"
                dense
                @input="onAllUsersToggle"
              />
              <div v-if="!editItem.all_users" class="q-mt-xs q-ml-sm">
                <div class="text-caption text-grey q-mb-xs">Visible to:</div>
                <div
                  class="column q-gutter-xs"
                  style="max-height: 200px; overflow-y: auto"
                >
                  <q-checkbox
                    v-for="u in userOptions"
                    :key="u.value"
                    :value="editItem.user_ids.includes(u.value)"
                    @input="toggleUser(u.value)"
                    :label="u.label"
                    :disable="u.value === $store.state.user.user_id"
                    dense
                    size="sm"
                  />
                </div>
              </div>
            </div>
          </q-form>
        </div>
        <q-separator />
        <div class="row justify-end q-gutter-sm q-pa-md">
          <q-btn
            label="Cancel"
            color="primary"
            flat
            @click="showDialog = false"
          />
          <q-btn
            label="Submit"
            color="primary"
            :disable="
              !editItem.all_users &&
              (!editItem.user_ids || editItem.user_ids.length === 0)
            "
            @click="onSubmit"
          />
        </div>
      </q-card>
    </q-dialog>

    <!-- Category Management dialog -->
    <q-dialog
      persistent
      v-model="showCategoryDialog"
      full-height
      position="top"
    >
      <q-card style="width: 400px; max-width: 95vw">
        <div class="text-h6 text-center q-pa-md">Manage Categories</div>
        <q-separator />
        <div
          class="scroll overflow-auto q-px-md"
          style="max-height: calc(100vh - 150px)"
        >
          <draggable
            :value="filteredLocalCategories"
            handle=".cat-drag"
            @input="onCategoryDrag"
            @end="onCategoryReorder"
          >
            <div v-for="(cat, index) in filteredLocalCategories" :key="cat.id">
              <q-separator v-if="index > 0" />
              <div class="row items-center q-py-sm">
                <q-icon
                  name="drag_indicator"
                  size="24px"
                  class="cat-drag text-grey-5 q-mr-sm"
                />
                <div class="col">
                  <div v-if="!cat.display_default" class="q-mb-xs">
                    <q-badge color="grey-3" text-color="black" label="Hidden" />
                  </div>
                  <div class="row items-center">
                    <span
                      class="color-swatch q-mr-xs"
                      :class="'bg-' + (cat.color || 'secondary')"
                    />
                    {{ cat.item }}
                  </div>
                </div>
                <q-btn
                  unelevated
                  icon="edit"
                  size="sm"
                  color="grey-2"
                  text-color="primary"
                  @click="editCategory(cat)"
                />
                <q-btn
                  unelevated
                  icon="delete"
                  size="sm"
                  color="grey-2"
                  text-color="negative"
                  class="q-ml-xs"
                  :disable="categoryItems(cat.id).length > 0"
                  @click="deleteCategory(cat)"
                />
              </div>
            </div>
          </draggable>
        </div>
        <q-separator />
        <div class="row justify-end q-gutter-sm q-pa-md">
          <q-btn
            flat
            label="Cancel"
            color="primary"
            @click="showCategoryDialog = false"
          />
          <q-btn label="Add" color="primary" @click="openAddCategory" />
        </div>
      </q-card>
    </q-dialog>

    <!-- Add / Edit Category dialog -->
    <q-dialog persistent v-model="showAddCategoryDialog">
      <q-card style="width: 400px; max-width: 95vw">
        <q-card-section>
          <div class="text-h6 text-center">
            {{ editCategoryItem.id ? 'Edit' : 'Add' }}
            Category
          </div>
        </q-card-section>
        <q-separator />
        <q-card-section>
          <div class="row q-gutter-md q-mb-sm">
            <q-checkbox
              v-model="editCategoryItem.display_default"
              label="Display on Main Board"
              :true-value="1"
              :false-value="0"
              dense
              size="sm"
            />
          </div>
          <div class="q-mb-sm">
            <div class="text-caption text-grey q-mb-xs">Color</div>
            <div class="row q-gutter-sm">
              <q-btn
                v-for="c in colorOptions"
                :key="c.value"
                round
                unelevated
                size="sm"
                :color="c.value"
                :outline="(editCategoryItem.color || 'secondary') !== c.value"
                @click="editCategoryItem.color = c.value"
              >
                <q-tooltip>{{ c.label }}</q-tooltip>
              </q-btn>
            </div>
          </div>
          <q-input
            v-model="editCategoryItem.item"
            outlined
            label="Name"
            maxlength="255"
            counter
          />
          <div
            class="q-mt-sm q-pa-sm rounded-borders"
            style="border: 1px solid #e0e0e0"
          >
            <q-checkbox
              v-model="editCategoryItem.all_users"
              label="All Users"
              :true-value="1"
              :false-value="0"
              dense
              size="sm"
              @input="onCatAllUsersToggle"
            />
            <div v-if="!editCategoryItem.all_users" class="q-mt-xs q-ml-sm">
              <div class="text-caption text-grey q-mb-xs">Visible to:</div>
              <div
                class="column q-gutter-xs"
                style="max-height: 200px; overflow-y: auto"
              >
                <q-checkbox
                  v-for="u in userOptions"
                  :key="u.value"
                  :value="editCategoryItem.user_ids.includes(u.value)"
                  @input="toggleCatUser(u.value)"
                  :label="u.label"
                  :disable="u.value === $store.state.user.user_id"
                  dense
                  size="sm"
                />
              </div>
            </div>
          </div>
        </q-card-section>
        <q-separator />
        <q-card-section class="row justify-end q-gutter-sm">
          <q-btn
            flat
            label="Cancel"
            color="primary"
            @click="showAddCategoryDialog = false"
          />
          <q-btn label="Submit" color="primary" @click="submitCategory" />
        </q-card-section>
      </q-card>
    </q-dialog>

    <!-- Delete Confirmation dialog -->
    <q-dialog persistent v-model="showDeleteDialog">
      <q-card style="width: 400px; max-width: 95vw">
        <q-card-section>
          <div
            class="text-subtitle1 text-center ellipsis"
            :class="{ 'text-weight-bold': deleteItem.hasTitle }"
          >
            {{ deleteItem.title }}
          </div>
        </q-card-section>
        <q-separator />
        <q-card-section>
          <div class="text-center">
            {{ deleteItem.message }}
          </div>
        </q-card-section>
        <q-separator />
        <q-card-section class="row justify-end q-gutter-sm">
          <q-btn
            flat
            label="Cancel"
            color="primary"
            @click="showDeleteDialog = false"
          />
          <q-btn
            label="Delete"
            icon="delete"
            color="negative"
            @click="onDeleteConfirm"
          />
        </q-card-section>
      </q-card>
    </q-dialog>

    <!-- Attachments dialog -->
    <q-dialog v-model="showAttachmentDialog" maximized>
      <q-card>
        <div class="row items-center q-pa-sm">
          <q-btn
            outline
            label="Back"
            color="primary"
            icon="arrow_back"
            padding="xs sm"
            @click="showAttachmentDialog = false"
          />
          <q-space />
          <div class="text-h6">
            {{ attachmentBulletin.title }}
          </div>
        </div>
        <q-separator />
        <q-card-section>
          <Attachments
            v-if="showAttachmentDialog"
            :parentObj="attachmentBulletin"
            :parentType="'bulletin'"
          />
        </q-card-section>
      </q-card>
    </q-dialog>

    <!-- List Item Action dialog -->
    <q-dialog persistent v-model="showListItemDialog">
      <q-card style="width: 400px; max-width: 95vw">
        <q-card-section>
          <div class="text-subtitle1 text-center ellipsis">
            {{ listItemAction.text }}
          </div>
        </q-card-section>
        <q-separator />
        <q-card-section class="row no-wrap justify-end q-gutter-sm">
          <q-btn
            flat
            dense
            label="Cancel"
            color="primary"
            padding="xs sm"
            @click="showListItemDialog = false"
          />
          <q-btn
            unelevated
            dense
            label="Move"
            icon="drive_file_move"
            color="primary"
            padding="xs sm"
            @click="openMoveDialog"
          />
          <q-btn
            unelevated
            dense
            label="Delete"
            icon="delete"
            color="negative"
            padding="xs sm"
            @click="onListItemDelete"
          />
        </q-card-section>
      </q-card>
    </q-dialog>

    <!-- Move List Item dialog -->
    <q-dialog persistent v-model="showMoveDialog">
      <q-card style="width: 400px; max-width: 95vw">
        <q-card-section>
          <div class="text-h6 text-center">Move to...</div>
        </q-card-section>
        <q-separator />
        <q-card-section class="scroll" style="max-height: 60vh">
          <template v-for="cat in accessibleCategories">
            <div
              v-if="moveTargets(cat.id).length > 0"
              :key="'mv-' + cat.id"
              class="q-mb-sm"
            >
              <div
                class="text-caption text-weight-bold q-mb-xs"
                :style="'color: ' + categoryHex(cat)"
              >
                {{ cat.item }}
              </div>
              <q-list dense separator>
                <q-item
                  v-for="b in moveTargets(cat.id)"
                  :key="'mv-b-' + b.id"
                  clickable
                  v-close-popup
                  @click="moveListItem(b)"
                >
                  <q-item-section>
                    <q-item-label class="ellipsis">
                      {{ b.title || stripHtml(b.body) }}
                    </q-item-label>
                  </q-item-section>
                </q-item>
              </q-list>
            </div>
          </template>
        </q-card-section>
        <q-separator />
        <q-card-section class="row justify-end">
          <q-btn
            flat
            label="Cancel"
            color="primary"
            @click="showMoveDialog = false"
          />
        </q-card-section>
      </q-card>
    </q-dialog>
  </q-page>
</template>

<script>
import store from '@/store';
import { decoder } from '@/lib/helpers';
import { parseTimeStamp } from '@/lib/date-utils';
import { colors } from 'quasar';
import draggable from 'vuedraggable';
import Attachments from '@/components/Attachments.vue';
import Sortable from 'sortablejs';

function initListSort(el, cb) {
  if (el._sortables) {
    el._sortables.forEach((s) => s.destroy());
  }
  el._sortables = [];
  el._dragging = false;
  el.querySelectorAll('.li-drag-handle').forEach((h) => h.remove());
  el.querySelectorAll('li').forEach((li) => {
    const h = document.createElement('i');
    h.className = 'li-drag-handle material-icons';
    h.textContent = 'drag_indicator';
    const span = document.createElement('span');
    span.className = 'li-text';
    while (li.firstChild) span.appendChild(li.firstChild);
    li.appendChild(span);
    li.prepend(h);
  });
  el.querySelectorAll('ul, ol').forEach((list) => {
    el._sortables.push(
      Sortable.create(list, {
        animation: 150,
        handle: '.li-drag-handle',
        onStart() {
          el._dragging = true;
        },
        onEnd() {
          el._dragging = false;
          const clone = el.cloneNode(true);
          clone.querySelectorAll('.li-drag-handle').forEach((h) => h.remove());
          clone
            .querySelectorAll('.li-text')
            .forEach((s) => s.replaceWith(...s.childNodes));
          cb(clone.innerHTML);
        }
      })
    );
  });
}

export default {
  components: { draggable, Attachments },
  directives: {
    sortableList: {
      inserted(el, binding) {
        initListSort(el, binding.value);
      },
      componentUpdated(el, binding) {
        if (el._dragging) return;
        initListSort(el, binding.value);
      },
      unbind(el) {
        if (el._sortables) {
          el._sortables.forEach((s) => s.destroy());
        }
      }
    }
  },
  props: {
    adminMode: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      selectedCategories: [],
      filtersInitialized: false,
      showDialog: false,
      showCategoryDialog: false,
      showAddCategoryDialog: false,
      showDeleteDialog: false,
      showHidden: false,
      deleteItem: { title: '', message: '', callback: null },
      showAttachmentDialog: false,
      editItem: this.newBulletin(),
      colorOptions: [
        { label: 'Teal', value: 'secondary' },
        { label: 'Blue', value: 'primary' },
        { label: 'Red', value: 'accent' },
        { label: 'Purple', value: 'deep-purple' },
        { label: 'Orange', value: 'orange' }
      ],
      editCategoryItem: {
        id: null,
        item: '',
        admin_only: 1,
        display_default: 1,
        color: 'secondary',
        all_users: 1,
        user_ids: []
      },
      localBulletins: [],
      localCategories: [],
      groupedBulletins: {},
      attachmentBulletin: { attachments: [] },
      collapsedCategories: [],
      expandedBulletins: [],
      showListItemDialog: false,
      listItemAction: {
        bulletin: null,
        idx: -1,
        text: ''
      },
      showMoveDialog: false
    };
  },
  computed: {
    bulletins() {
      return this.$store.state.bulletins;
    },
    categories() {
      return this.$store.state.bulletinCategories;
    },
    accessibleCategories() {
      return this.categories
        .filter((c) => {
          if (this.adminMode) return !!c.admin_only;
          return !c.admin_only;
        })
        .filter((c) => this.isCategoryVisibleToUser(c));
    },
    allSelected() {
      return (
        this.accessibleCategories.length > 0 &&
        this.accessibleCategories.every((c) =>
          this.selectedCategories.includes(c.id)
        )
      );
    },
    filteredLocalCategories() {
      return this.localCategories.filter((c) => {
        if (this.adminMode) return !!c.admin_only;
        return !c.admin_only;
      });
    },
    visibleCategories() {
      return this.accessibleCategories
        .filter((c) => this.selectedCategories.includes(c.id))
        .filter((c) => this.categoryItems(c.id).length > 0);
    },
    userOptions() {
      return this.$store.state.userList
        .map((u) => ({
          label: u.display_name,
          value: u.user_id
        }))
        .sort((a, b) => a.label.localeCompare(b.label));
    },
    FarmOptions() {
      const ret = [];
      let farmList;
      if (this.$store.getters.isSuperAdmin) {
        farmList = this.$store.state.farmList;
      } else {
        farmList = this.$store.state.farmList.filter((e) => {
          return this.$store.state.user.farms.find((f) => f === e.id);
        });
      }
      for (let i = 0; i < farmList.length; i++) {
        if (farmList[i].is_active > 0) {
          ret.push({
            label: decoder(farmList[i].farm_name),
            value: farmList[i].id
          });
        }
      }
      return ret;
    }
  },
  watch: {
    bulletins: {
      immediate: true,
      handler(val) {
        this.localBulletins = JSON.parse(JSON.stringify(val));
        this.rebuildGroups();
      }
    },
    categories: {
      immediate: true,
      handler(val) {
        this.localCategories = JSON.parse(JSON.stringify(val));
        this.rebuildGroups();
        if (!this.filtersInitialized && val.length > 0) {
          this.filtersInitialized = true;
          this.selectedCategories = this.accessibleCategories
            .filter((c) => c.display_default)
            .map((c) => c.id);
        } else {
          // Auto-select newly added categories
          this.accessibleCategories.forEach((c) => {
            if (c.display_default && !this.selectedCategories.includes(c.id)) {
              this.selectedCategories.push(c.id);
            }
          });
        }
      }
    },
    showHidden() {
      this.rebuildGroups();
    },
    adminMode() {
      this.selectedCategories = this.accessibleCategories
        .filter((c) => c.display_default)
        .map((c) => c.id);
      this.rebuildGroups();
    }
  },
  methods: {
    toggleAll() {
      if (this.allSelected) {
        this.selectedCategories = [];
      } else {
        this.selectedCategories = this.accessibleCategories.map((c) => c.id);
      }
    },
    toggleCategory(catId) {
      const idx = this.selectedCategories.indexOf(catId);
      if (idx >= 0) {
        this.selectedCategories.splice(idx, 1);
      } else {
        this.selectedCategories.push(catId);
      }
    },
    stripHtml(html) {
      if (!html) return '';
      const div = document.createElement('div');
      div.innerHTML = html;
      div.querySelectorAll('li').forEach((li) => li.prepend(' \u00B7 '));
      return (div.textContent || '').replace(/^\s*\u00B7\s*/, '').trim();
    },
    isCategoryCollapsed(catId) {
      return this.collapsedCategories.includes(catId);
    },
    isBulletinCollapsed(catId, bulletinId) {
      return (
        this.collapsedCategories.includes(catId) &&
        !this.expandedBulletins.includes(bulletinId)
      );
    },
    toggleCategoryCollapse(catId) {
      const idx = this.collapsedCategories.indexOf(catId);
      if (idx >= 0) {
        this.collapsedCategories.splice(idx, 1);
      } else {
        this.collapsedCategories.push(catId);
      }
      // Clear individual expansions
      const items = this.categoryItems(catId);
      items.forEach((b) => {
        const i = this.expandedBulletins.indexOf(b.id);
        if (i >= 0) {
          this.expandedBulletins.splice(i, 1);
        }
      });
    },
    toggleBulletinExpand(bulletinId) {
      const idx = this.expandedBulletins.indexOf(bulletinId);
      if (idx >= 0) {
        this.expandedBulletins.splice(idx, 1);
      } else {
        this.expandedBulletins.push(bulletinId);
      }
    },
    categoryItems(catId) {
      return this.groupedBulletins[catId] || [];
    },
    categoryName(catId) {
      const cat = this.categories.find((c) => c.id === catId);
      return cat ? cat.item : '';
    },
    categoryColor(catId) {
      const cat = this.categories.find((c) => c.id === catId);
      return cat && cat.color ? cat.color : 'secondary';
    },
    categoryHex(cat) {
      const name = cat.color || 'secondary';
      return colors.getPaletteColor(name);
    },
    colorLabel(color) {
      const opt = this.colorOptions.find(
        (c) => c.value === (color || 'secondary')
      );
      return opt ? opt.label : 'Teal';
    },
    isVisibleToUser(b) {
      if (b.all_users || b.all_users == null) return true;
      if (!b.user_ids || b.user_ids.length === 0) return true;
      const uid = this.$store.state.user.user_id;
      return b.user_ids.some((id) => id == uid);
    },
    bulletinAccessLabel(b) {
      if (b.all_users || b.all_users == null) {
        return 'All Users';
      }
      if (!b.user_ids || b.user_ids.length === 0) {
        return 'All Users';
      }
      const list = this.$store.state.userList;
      return b.user_ids
        .map((id) => {
          const u = list.find((x) => x.user_id == id);
          return u ? u.display_name : '';
        })
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b))
        .join(', ');
    },
    onAllUsersToggle(val) {
      if (!val) {
        const uid = this.$store.state.user.user_id;
        if (!this.editItem.user_ids.includes(uid)) {
          this.editItem.user_ids.push(uid);
        }
      }
    },
    toggleUser(userId) {
      const uid = this.$store.state.user.user_id;
      if (userId === uid) return;
      const idx = this.editItem.user_ids.indexOf(userId);
      if (idx >= 0) {
        this.editItem.user_ids.splice(idx, 1);
      } else {
        this.editItem.user_ids.push(userId);
      }
    },
    onCatAllUsersToggle(val) {
      if (!val) {
        const uid = this.$store.state.user.user_id;
        if (!this.editCategoryItem.user_ids.includes(uid)) {
          this.editCategoryItem.user_ids.push(uid);
        }
      }
    },
    toggleCatUser(userId) {
      const uid = this.$store.state.user.user_id;
      if (userId === uid) return;
      const ids = this.editCategoryItem.user_ids;
      const idx = ids.indexOf(userId);
      if (idx >= 0) {
        ids.splice(idx, 1);
      } else {
        ids.push(userId);
      }
    },
    isCategoryVisibleToUser(cat) {
      if (cat.all_users || cat.all_users == null) {
        return true;
      }
      if (!cat.user_ids || cat.user_ids.length === 0) {
        return true;
      }
      const uid = this.$store.state.user.user_id;
      return cat.user_ids.some((id) => id == uid);
    },
    handleBodyClick(event, bulletin) {
      if (event.target.closest('.li-drag-handle')) return;
      const li = event.target.closest('li');
      if (!li) return;
      event.preventDefault();
      const container = event.currentTarget;
      const allLis = container.querySelectorAll('li');
      const idx = Array.from(allLis).indexOf(li);
      if (idx < 0) return;
      const clone = li.cloneNode(true);
      clone.querySelectorAll('.li-drag-handle').forEach((h) => h.remove());
      const raw = clone.textContent.trim();
      const text = raw.substring(0, 50) + (raw.length > 50 ? '...' : '');
      this.listItemAction = {
        bulletin,
        idx,
        text
      };
      this.showListItemDialog = true;
    },
    onListReorder(bulletin, html) {
      bulletin.body = html;
      store.dispatch('updateBulletin', { bulletin });
    },
    removeListItem(bulletin, idx) {
      const div = document.createElement('div');
      div.innerHTML = bulletin.body;
      const items = div.querySelectorAll('li');
      if (!items[idx]) return;
      const parent = items[idx].parentElement;
      items[idx].remove();
      if (parent && parent.children.length === 0) {
        parent.remove();
      }
      bulletin.body = div.innerHTML;
      store.dispatch('updateBulletin', { bulletin });
    },
    onListItemDelete() {
      this.showListItemDialog = false;
      this.removeListItem(
        this.listItemAction.bulletin,
        this.listItemAction.idx
      );
    },
    openMoveDialog() {
      this.showListItemDialog = false;
      this.showMoveDialog = true;
    },
    moveTargets(catId) {
      const src = this.listItemAction.bulletin;
      return this.categoryItems(catId).filter((b) => !src || b.id !== src.id);
    },
    moveListItem(target) {
      const src = this.listItemAction.bulletin;
      const idx = this.listItemAction.idx;
      const srcDiv = document.createElement('div');
      srcDiv.innerHTML = src.body;
      const srcItems = srcDiv.querySelectorAll('li');
      if (!srcItems[idx]) return;
      const liEl = srcItems[idx];
      const srcParent = liEl.parentElement;
      liEl.remove();
      if (srcParent && srcParent.children.length === 0) {
        srcParent.remove();
      }
      src.body = srcDiv.innerHTML;
      const tgtDiv = document.createElement('div');
      tgtDiv.innerHTML = target.body;
      let list = tgtDiv.querySelector('ul, ol');
      if (!list) {
        list = document.createElement('ul');
        tgtDiv.appendChild(list);
      }
      list.appendChild(liEl);
      target.body = tgtDiv.innerHTML;
      store.dispatch('updateBulletin', {
        bulletin: src
      });
      store.dispatch('updateBulletin', {
        bulletin: target
      });
      this.showMoveDialog = false;
      this.$q.notify({
        message: 'Item moved',
        color: 'primary'
      });
    },
    rebuildGroups() {
      const validCats = new Set(this.localCategories.map((c) => c.id));
      // Remove stale categories
      for (const key of Object.keys(this.groupedBulletins)) {
        if (!validCats.has(parseInt(key))) {
          this.$delete(this.groupedBulletins, key);
        }
      }
      // Update / add categories
      for (const cat of this.localCategories) {
        const items = this.localBulletins
          .filter((b) => b.category_id === cat.id)
          .filter((b) => this.showHidden || b.display_default !== 0)
          .filter((b) => this.isVisibleToUser(b))
          .sort((a, b) => a.sort_order - b.sort_order);
        if (!this.groupedBulletins[cat.id]) {
          this.$set(this.groupedBulletins, cat.id, items);
        } else {
          const arr = this.groupedBulletins[cat.id];
          arr.splice(0, arr.length, ...items);
        }
      }
    },
    onDragEnd() {
      const updated = [];
      for (const cat of this.categories) {
        const items = this.groupedBulletins[cat.id] || [];
        items.forEach((item, i) => {
          if (item.category_id !== cat.id || item.sort_order !== i) {
            item.category_id = cat.id;
            item.sort_order = i;
            updated.push({
              id: item.id,
              sort_order: i,
              category_id: cat.id
            });
          }
        });
      }
      if (updated.length > 0) {
        store.dispatch('reorderBulletins', {
          items: updated
        });
      }
    },
    /* Category management */
    openAddCategory() {
      this.editCategoryItem = {
        id: null,
        item: '',
        admin_only: this.adminMode ? 1 : 0,
        display_default: 1,
        color: 'secondary',
        all_users: 1,
        user_ids: []
      };
      this.showAddCategoryDialog = true;
    },
    editCategory(cat) {
      this.editCategoryItem = JSON.parse(JSON.stringify(cat));
      if (!this.editCategoryItem.user_ids) {
        this.editCategoryItem.user_ids = [];
      }
      if (this.editCategoryItem.all_users == null) {
        this.editCategoryItem.all_users = 1;
      }
      this.showAddCategoryDialog = true;
    },
    submitCategory() {
      const name =
        this.editCategoryItem.item.trim().charAt(0).toUpperCase() +
        this.editCategoryItem.item.trim().slice(1);
      if (!name) return;
      this.editCategoryItem.item = name;
      if (this.editCategoryItem.id) {
        store.dispatch('updateBulletinCategory', {
          category: this.editCategoryItem
        });
      } else {
        store.dispatch('createBulletinCategory', {
          category: {
            item: name,
            view_order: this.localCategories.length,
            admin_only: this.editCategoryItem.admin_only,
            display_default: this.editCategoryItem.display_default,
            color: this.editCategoryItem.color || 'secondary',
            all_users: this.editCategoryItem.all_users,
            user_ids: this.editCategoryItem.user_ids || []
          }
        });
      }
      this.showAddCategoryDialog = false;
    },
    deleteCategory(cat) {
      this.deleteItem = {
        title: cat.item,
        message: 'Delete this category?',
        callback: () => {
          store.dispatch('deleteBulletinCategory', {
            category: cat
          });
        }
      };
      this.showDeleteDialog = true;
    },
    onCategoryDrag(reordered) {
      // Merge reordered filtered list back into localCategories
      const otherCats = this.localCategories.filter((c) => {
        if (this.adminMode) return !c.admin_only;
        return !!c.admin_only;
      });
      this.localCategories = [...reordered, ...otherCats];
    },
    onCategoryReorder() {
      const items = this.localCategories.map((c, i) => ({
        id: c.id,
        view_order: i
      }));
      store.commit('SET_BULLETIN_CATS_ALL', {
        items: this.localCategories
      });
      store.dispatch('reorderBulletinCategories', {
        items
      });
    },
    /* Attachments */
    openAttachments(bulletin) {
      this.attachmentBulletin = JSON.parse(JSON.stringify(bulletin));
      this.showAttachmentDialog = true;
    },
    /* Bulletin CRUD */
    newBulletin() {
      const defaultCatId =
        this.categories && this.categories.length > 0
          ? this.categories[0].id
          : 0;
      return {
        id: 'new',
        user_id: this.$store ? this.$store.state.user.user_id : 0,
        farm_id: null,
        title: '',
        body: '',
        category_id: defaultCatId,
        sort_order: 0,
        date: Math.floor(Date.now() / 1000),
        all_users: 0,
        user_ids: [this.$store ? this.$store.state.user.user_id : 0],
        display_default: 1
      };
    },
    formatDate(ts) {
      return parseTimeStamp(ts, 'short');
    },
    farmName(farmId) {
      if (!farmId) return '';
      const farm = this.$store.state.farmList.find((f) => f.id === farmId);
      return farm ? decoder(farm.farm_name) : '';
    },
    addBulletin(categoryId) {
      this.editItem = this.newBulletin();
      if (categoryId) {
        this.editItem.category_id = categoryId;
      }
      this.showDialog = true;
    },
    changeCategory(bulletin, catId) {
      if (bulletin.category_id === catId) return;
      bulletin.category_id = catId;
      store.dispatch('updateBulletin', { bulletin });
    },
    editBulletin(bulletin) {
      this.editItem = JSON.parse(JSON.stringify(bulletin));
      if (!this.editItem.user_ids) {
        this.editItem.user_ids = [];
      }
      if (this.editItem.all_users == null) {
        this.editItem.all_users = 1;
      }
      if (this.editItem.display_default == null) {
        this.editItem.display_default = 1;
      }
      this.showDialog = true;
    },
    onSubmit() {
      const dispatch =
        this.editItem.id === 'new' ? 'createBulletin' : 'updateBulletin';

      if (dispatch === 'createBulletin') {
        this.editItem.date = Math.floor(Date.now() / 1000);
      }

      store
        .dispatch(dispatch, {
          bulletin: this.editItem
        })
        .then((results) => {
          if (results.sent === false || results.reasonCode !== 0) {
            this.$finishResult.handleResultsAsync(results).then((response) => {
              if (response === 'retry') {
                this.onSubmit();
              }
            });
          } else {
            this.$q.notify({
              message: 'Bulletin Saved',
              color: 'primary'
            });
          }
        });

      this.showDialog = false;
    },
    confirmDelete(bulletin) {
      this.deleteItem = {
        hasTitle: !!bulletin.title,
        title: bulletin.title || this.stripHtml(bulletin.body),
        message: 'Delete this bulletin?',
        callback: () => {
          this.dispatchDelete(bulletin);
        }
      };
      this.showDeleteDialog = true;
    },
    onDeleteConfirm() {
      if (this.deleteItem.callback) {
        this.deleteItem.callback();
      }
      this.showDeleteDialog = false;
    },
    dispatchDelete(bulletin) {
      store.dispatch('deleteBulletin', { bulletin }).then((results) => {
        this.$finishResult.handleResultsAsync(results).then((response) => {
          if (response === 'retry') {
            this.dispatchDelete(bulletin);
          }
        });
      });
    }
  }
};
</script>

<style scoped>
.drag-handle {
  font-size: 18px;
  cursor: grab;
}
.drag-handle:active,
.cat-drag:active {
  cursor: grabbing;
}
.cat-drag {
  cursor: grab;
}
.bulletin-col {
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
}
.bulletin-body >>> ul,
.bulletin-body >>> ol {
  margin: 0;
  padding-left: 40px;
}
.bulletin-body >>> li {
  position: relative;
}
.bulletin-body >>> p {
  margin: 0;
}
@media (hover: hover) {
  .bulletin-body >>> .li-text {
    cursor: pointer;
  }
  .bulletin-body >>> .li-text:hover {
    text-decoration: line-through;
  }
}
.bulletin-body >>> .li-drag-handle {
  position: absolute;
  left: -36px;
  top: 1px;
  cursor: grab;
  color: #bbb;
  font-size: 16px;
  user-select: none;
}
.color-swatch {
  display: inline-block;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  vertical-align: middle;
}
</style>
